import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { fileUrl } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Bookmark, Anchor, Flag, Palette, Type, Image as ImageIcon, LayoutGrid, Package,
  FileText, Settings, LifeBuoy, User as UserIcon, Sparkles, PenLine, Wrench, Image as ImageTool,
  Menu as MenuIcon, Zap, LogOut, Building2,
} from "lucide-react";

const pageIcon = (name) => {
  const map = {
    "Introductie": Bookmark, "Introduction": Bookmark,
    "Het merk": Anchor, "Brand Strategy": Anchor, "Merkstrategie": Anchor, "The brand": Anchor,
    "Het logo": Flag, "Logo": Flag,
    "Kleuren": Palette, "Colors": Palette, "Color": Palette,
    "Typografie": Type, "Typography": Type,
    "Art direction": ImageIcon, "Art Direction": ImageIcon,
    "Merksysteem": LayoutGrid, "Brand System": LayoutGrid,
    "Concept middelen": Package, "Applications": Package,
  };
  return map[name] || FileText;
};

export default function BottomNav({
  brand, pages, activePageId, activeTool, onSelectPage, onSelectTool, user, role,
}) {
  const { logout } = useAuth();
  const { t } = useI18n();
  const [open, setOpen] = useState(null);

  const close = () => setOpen(null);
  const TOOLS = [
    { key: "agent", label: t("sidebar.tool_agent"), badge: t("sidebar.badge_soon"), icon: Sparkles },
    { key: "writer", label: t("sidebar.tool_writer"), badge: t("sidebar.badge_pro"), icon: PenLine },
    { key: "creator", label: t("sidebar.tool_creator"), badge: t("sidebar.badge_pro"), icon: ImageTool },
    { key: "tool", label: t("sidebar.tool_tool"), badge: t("sidebar.badge_pro"), icon: Wrench, disabled: true },
  ];
  const avatarUrl = user?.avatar_file_id ? fileUrl(user.avatar_file_id) : user?.picture;

  return (
    <nav
      data-testid="bottom-nav"
      className="md:hidden fixed bottom-0 inset-x-0 z-[55] bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.04)]"
    >
      <div className="grid grid-cols-5 h-14 safe-area-pb">
        {/* Pages */}
        <Popover open={open === "pages"} onOpenChange={(v) => setOpen(v ? "pages" : null)}>
          <PopoverTrigger asChild>
            <button data-testid="bottom-nav-pages" className="flex flex-col items-center justify-center gap-0.5 hover:bg-gray-50">
              <MenuIcon className="w-5 h-5" strokeWidth={1.75} />
              <span className="text-[9px] font-medium">Brand</span>
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" align="start" sideOffset={8} className="w-[calc(100vw-1rem)] max-w-sm p-1.5 bg-white rounded-xl shadow-xl">
            <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-gray-500">{t("sidebar.section_the_brand")}</div>
            {pages.map((p) => {
              const Icon = pageIcon(p.name);
              const active = p.id === activePageId && !activeTool;
              return (
                <button
                  key={p.id}
                  data-testid={`bottom-page-${p.id}`}
                  onClick={() => { onSelectPage(p.id); close(); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-[14px] ${active ? "bg-gray-100 font-medium" : "hover:bg-gray-50"}`}
                >
                  <Icon className="w-4 h-4 shrink-0" strokeWidth={1.75} />
                  <span className="truncate">{p.name}</span>
                </button>
              );
            })}
          </PopoverContent>
        </Popover>

        {/* Tools */}
        <Popover open={open === "tools"} onOpenChange={(v) => setOpen(v ? "tools" : null)}>
          <PopoverTrigger asChild>
            <button data-testid="bottom-nav-tools" className="flex flex-col items-center justify-center gap-0.5 hover:bg-gray-50">
              <Zap className="w-5 h-5" strokeWidth={1.75} />
              <span className="text-[9px] font-medium">Tools</span>
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" align="center" sideOffset={8} className="w-[calc(100vw-1rem)] max-w-sm p-1.5 bg-white rounded-xl shadow-xl">
            <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-gray-500">{t("sidebar.section_the_tools")}</div>
            {TOOLS.map((tItem) => {
              const active = activeTool === tItem.key;
              return (
                <button
                  key={tItem.key}
                  data-testid={`bottom-tool-${tItem.key}`}
                  disabled={tItem.disabled}
                  onClick={() => { if (tItem.disabled) return; onSelectTool(tItem.key); close(); }}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-[14px] ${active ? "bg-gray-100 font-medium" : "hover:bg-gray-50"} ${tItem.disabled ? "opacity-60" : ""}`}
                >
                  <span className="flex items-center gap-2.5">
                    <tItem.icon className="w-4 h-4 shrink-0" strokeWidth={1.75} /> {tItem.label}
                  </span>
                  {tItem.badge && (
                    <span className="text-[9px] font-normal uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#dcc9f3] text-[#5b3a9e]">{tItem.badge}</span>
                  )}
                </button>
              );
            })}
          </PopoverContent>
        </Popover>

        {/* Settings */}
        <button
          data-testid="bottom-nav-settings"
          onClick={() => onSelectTool("settings", "appearance")}
          className={`flex flex-col items-center justify-center gap-0.5 hover:bg-gray-50 ${activeTool === "settings" ? "text-[#1a1a1a] font-medium" : "text-gray-600"}`}
        >
          <Settings className="w-5 h-5" strokeWidth={1.75} />
          <span className="text-[9px] font-medium">{t("sidebar.settings")}</span>
        </button>

        {/* Support */}
        <button
          data-testid="bottom-nav-support"
          onClick={() => onSelectTool("support")}
          className={`flex flex-col items-center justify-center gap-0.5 hover:bg-gray-50 ${activeTool === "support" ? "text-[#1a1a1a] font-medium" : "text-gray-600"}`}
        >
          <LifeBuoy className="w-5 h-5" strokeWidth={1.75} />
          <span className="text-[9px] font-medium">{t("sidebar.support")}</span>
        </button>

        {/* Profile */}
        <Popover open={open === "profile"} onOpenChange={(v) => setOpen(v ? "profile" : null)}>
          <PopoverTrigger asChild>
            <button data-testid="bottom-nav-profile" className="flex flex-col items-center justify-center gap-0.5 hover:bg-gray-50">
              {avatarUrl ? (
                <img src={avatarUrl} alt="" className="w-5 h-5 rounded object-cover" />
              ) : (
                <UserIcon className="w-5 h-5" strokeWidth={1.75} />
              )}
              <span className="text-[9px] font-medium">{t("sidebar.profile_label")}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" align="end" sideOffset={8} className="w-64 p-1.5 bg-white rounded-xl shadow-xl">
            <div className="px-3 py-2 border-b mb-1">
              <div className="text-[10px] uppercase tracking-widest text-gray-500">{t("sidebar.the_brand_hub")}</div>
              <div className="text-sm font-medium truncate">{brand?.name || "—"}</div>
            </div>
            <button
              data-testid="bottom-nav-profile-link"
              onClick={() => { onSelectTool("settings", "profile"); close(); }}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm hover:bg-gray-50"
            >
              <UserIcon className="w-4 h-4" strokeWidth={1.75} /> {t("sidebar.profile_label")}
            </button>
            <Link
              to="/brands"
              data-testid="bottom-switch-brand"
              onClick={close}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm hover:bg-gray-50"
            >
              <Building2 className="w-4 h-4" strokeWidth={1.75} /> {t("sidebar.switch_brand")}
            </Link>
            <button
              data-testid="bottom-logout"
              onClick={logout}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm hover:bg-gray-50 text-red-700"
            >
              <LogOut className="w-4 h-4" /> {t("sidebar.logout")}
            </button>
          </PopoverContent>
        </Popover>
      </div>
    </nav>
  );
}
